// src/pages/AdminDashboard.jsx
// The editorial console, the admin-only page for managing users and dispatches.
// Reached at /admin-dash, guarded by PrivateRoute so only admins get here.
//
// Full CRUD over two resources:
//   - Fetches all users and all blogs on mount, with loading and error states.
//   - "+ New" opens a create form; Edit opens the same form prefilled.
//   - Remove deletes after a confirm.
//   - Every mutation refetches so the lists stay in sync with the database.
//
// Role display maps the stored values to the editorial voice: admin to Editor,
// user to Contributor. The create form maps them back when submitting.
//
// Visual approach: same editorial voice as the rest of the site
// (hairline rows and form brackets, mono labels, oxblood accents, no cards).

import { useState, useEffect } from "react";
import { getAllUsers, createUser, updateUser, deleteUser } from "../api/users";
import { getAllBlogs, createBlog, updateBlog, deleteBlog } from "../api/blogs";
import { useAuth } from "../context/AuthProvider";

// Map the stored role to its editorial label.
const roleLabel = (role) => (role === "admin" ? "Editor" : "Contributor");

// Format an ISO timestamp as a short date.
const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const inputClasses =
  "w-full bg-transparent border-0 border-b border-ink-20 py-3 text-body text-ink focus:outline-none focus:border-ink transition-colors";

const submitClasses =
  "label-mono border-b border-ink hover:text-oxblood hover:border-oxblood transition-colors py-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ink disabled:hover:border-ink";

const cancelClasses =
  "label-mono text-ink-70 hover:text-ink transition-colors py-2";

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Open form state. null means closed. Each carries its mode and field values.
  const [userForm, setUserForm] = useState(null);
  const [blogForm, setBlogForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch both lists. Reused after every mutation to keep the view in sync.
  async function loadData() {
    setLoading(true);
    setError("");
    try {
      const [usersRes, blogsRes] = await Promise.all([
        getAllUsers(),
        getAllBlogs(),
      ]);
      setUsers(usersRes.data);
      setBlogs(blogsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load the console.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  // --- Contributor forms ---

  function openNewUser() {
    setFormError("");
    setBlogForm(null);
    setUserForm({
      mode: "create",
      name: "",
      username: "",
      email: "",
      password: "",
      role: "user",
    });
  }

  function openEditUser(u) {
    setFormError("");
    setBlogForm(null);
    setUserForm({
      mode: "edit",
      id: u.id,
      name: u.name,
      username: u.username,
      email: u.email,
    });
  }

  function closeUserForm() {
    setUserForm(null);
    setFormError("");
  }

  async function handleSubmitUser(e) {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      if (userForm.mode === "create") {
        await createUser({
          name: userForm.name,
          username: userForm.username,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
        });
      } else {
        await updateUser(userForm.id, {
          name: userForm.name,
          username: userForm.username,
          email: userForm.email,
        });
      }
      closeUserForm();
      await loadData();
    } catch (err) {
      setFormError(
        err.response?.data?.error || "Failed to save the contributor.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteUser(id) {
    if (
      !window.confirm(
        "Remove this contributor? This also deletes their dispatches.",
      )
    ) {
      return;
    }
    try {
      await deleteUser(id);
      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to remove the contributor.",
      );
    }
  }

  // --- Dispatch forms ---

  function openNewBlog() {
    setFormError("");
    setUserForm(null);
    setBlogForm({
      mode: "create",
      title: "",
      content: "",
      user_id: currentUser?.id ?? users[0]?.id ?? "",
    });
  }

  function openEditBlog(b) {
    setFormError("");
    setUserForm(null);
    setBlogForm({ mode: "edit", id: b.id, title: b.title, content: b.content });
  }

  function closeBlogForm() {
    setBlogForm(null);
    setFormError("");
  }

  async function handleSubmitBlog(e) {
    e.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      if (blogForm.mode === "create") {
        await createBlog({
          title: blogForm.title,
          content: blogForm.content,
          user_id: blogForm.user_id,
        });
      } else {
        await updateBlog(blogForm.id, {
          title: blogForm.title,
          content: blogForm.content,
        });
      }
      closeBlogForm();
      await loadData();
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to save the dispatch.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteBlog(id) {
    if (!window.confirm("Remove this dispatch?")) {
      return;
    }
    try {
      await deleteBlog(id);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove the dispatch.");
    }
  }

  return (
    <>
      {/* Page header */}
      <section className="max-w-wide mx-auto px-6 pt-20 pb-12 md:pt-28 md:pb-16">
        <p className="label-mono">Internal</p>
        <h1 className="font-display text-h2 leading-tight font-medium mt-3">
          Editorial console
        </h1>
        <p className="text-body text-ink-70 leading-relaxed mt-6 max-w-2xl">
          Manage contributors and dispatches across the platform.
        </p>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* Error banner */}
      {error && (
        <section className="max-w-wide mx-auto px-6 pt-6">
          <p className="text-small text-oxblood">{error}</p>
        </section>
      )}

      {loading ? (
        <section className="max-w-wide mx-auto px-6 py-16 md:py-20">
          <p className="label-mono">Loading the console...</p>
        </section>
      ) : (
        <>
          {/* Contributors */}
          <section className="max-w-wide mx-auto px-6 py-16 md:py-20">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="label-mono">Contributors</p>
                <h2 className="font-display text-h3 font-medium leading-tight mt-2">
                  {users.length} on the masthead
                </h2>
              </div>
              <button
                type="button"
                onClick={openNewUser}
                className={submitClasses}
              >
                + New contributor
              </button>
            </div>

            {userForm && (
              <div className="border-y border-ink-20 py-8 mb-8">
                <p className="label-mono mb-6">
                  {userForm.mode === "create"
                    ? "New contributor"
                    : "Edit contributor"}
                </p>
                <form
                  onSubmit={handleSubmitUser}
                  className="space-y-6 max-w-lg"
                >
                  <div>
                    <label htmlFor="u-name" className="label-mono block mb-2">
                      Name
                    </label>
                    <input
                      id="u-name"
                      type="text"
                      value={userForm.name}
                      onChange={(e) =>
                        setUserForm({ ...userForm, name: e.target.value })
                      }
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="u-username"
                      className="label-mono block mb-2"
                    >
                      Username
                    </label>
                    <input
                      id="u-username"
                      type="text"
                      value={userForm.username}
                      onChange={(e) =>
                        setUserForm({ ...userForm, username: e.target.value })
                      }
                      required
                      minLength={3}
                      maxLength={50}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label htmlFor="u-email" className="label-mono block mb-2">
                      Email
                    </label>
                    <input
                      id="u-email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) =>
                        setUserForm({ ...userForm, email: e.target.value })
                      }
                      required
                      className={inputClasses}
                    />
                  </div>

                  {userForm.mode === "create" && (
                    <>
                      <div>
                        <label
                          htmlFor="u-password"
                          className="label-mono block mb-2"
                        >
                          Password
                        </label>
                        <input
                          id="u-password"
                          type="password"
                          value={userForm.password}
                          onChange={(e) =>
                            setUserForm({
                              ...userForm,
                              password: e.target.value,
                            })
                          }
                          required
                          minLength={8}
                          className={inputClasses}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="u-role"
                          className="label-mono block mb-2"
                        >
                          Role
                        </label>
                        <select
                          id="u-role"
                          value={userForm.role}
                          onChange={(e) =>
                            setUserForm({ ...userForm, role: e.target.value })
                          }
                          className={inputClasses}
                        >
                          <option value="user">Contributor</option>
                          <option value="admin">Editor</option>
                        </select>
                      </div>
                    </>
                  )}

                  {formError && (
                    <p className="text-small text-oxblood">{formError}</p>
                  )}

                  <div className="flex gap-6 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={submitClasses}
                    >
                      {saving
                        ? "Saving..."
                        : userForm.mode === "create"
                          ? "Create contributor →"
                          : "Save changes →"}
                    </button>
                    <button
                      type="button"
                      onClick={closeUserForm}
                      className={cancelClasses}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="border-t border-ink-20">
              {users.map((user) => (
                <div key={user.id} className="border-b border-ink-20 py-5">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-baseline">
                    <p className="label-mono md:col-span-1">No. {user.id}</p>
                    <p className="text-body text-ink md:col-span-3">
                      {user.name}
                    </p>
                    <p className="text-body text-ink-70 md:col-span-4">
                      {user.email}
                    </p>
                    <p className="label-mono md:col-span-2">
                      {roleLabel(user.role)}
                    </p>
                    <div className="md:col-span-2 md:text-right flex gap-4 md:justify-end">
                      <button
                        type="button"
                        onClick={() => openEditUser(user)}
                        className="label-mono hover:text-oxblood transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteUser(user.id)}
                        className="label-mono hover:text-oxblood transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <hr className="hairline max-w-wide mx-auto" />

          {/* Dispatches */}
          <section className="max-w-wide mx-auto px-6 py-16 md:py-20">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="label-mono">Dispatches</p>
                <h2 className="font-display text-h3 font-medium leading-tight mt-2">
                  {blogs.length} on file
                </h2>
              </div>
              <button
                type="button"
                onClick={openNewBlog}
                className={submitClasses}
              >
                + New dispatch
              </button>
            </div>

            {blogForm && (
              <div className="border-y border-ink-20 py-8 mb-8">
                <p className="label-mono mb-6">
                  {blogForm.mode === "create"
                    ? "New dispatch"
                    : "Edit dispatch"}
                </p>
                <form
                  onSubmit={handleSubmitBlog}
                  className="space-y-6 max-w-2xl"
                >
                  <div>
                    <label htmlFor="b-title" className="label-mono block mb-2">
                      Title
                    </label>
                    <input
                      id="b-title"
                      type="text"
                      value={blogForm.title}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, title: e.target.value })
                      }
                      required
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="b-content"
                      className="label-mono block mb-2"
                    >
                      Content
                    </label>
                    <textarea
                      id="b-content"
                      rows="6"
                      value={blogForm.content}
                      onChange={(e) =>
                        setBlogForm({ ...blogForm, content: e.target.value })
                      }
                      required
                      className={`${inputClasses} resize-none`}
                    />
                  </div>

                  {blogForm.mode === "create" && (
                    <div>
                      <label
                        htmlFor="b-author"
                        className="label-mono block mb-2"
                      >
                        Author
                      </label>
                      <select
                        id="b-author"
                        value={blogForm.user_id}
                        onChange={(e) =>
                          setBlogForm({ ...blogForm, user_id: e.target.value })
                        }
                        className={inputClasses}
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formError && (
                    <p className="text-small text-oxblood">{formError}</p>
                  )}

                  <div className="flex gap-6 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={submitClasses}
                    >
                      {saving
                        ? "Saving..."
                        : blogForm.mode === "create"
                          ? "Publish dispatch →"
                          : "Save changes →"}
                    </button>
                    <button
                      type="button"
                      onClick={closeBlogForm}
                      className={cancelClasses}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="border-t border-ink-20">
              {blogs.map((blog) => (
                <div key={blog.id} className="border-b border-ink-20 py-5">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-baseline">
                    <p className="label-mono md:col-span-1">No. {blog.id}</p>
                    <p className="text-body text-ink md:col-span-5">
                      {blog.title}
                    </p>
                    <p className="text-body text-ink-70 md:col-span-2">
                      {blog.user?.name}
                    </p>
                    <p className="label-mono md:col-span-2">
                      {formatDate(blog.created_at)}
                    </p>
                    <div className="md:col-span-2 md:text-right flex gap-4 md:justify-end">
                      <button
                        type="button"
                        onClick={() => openEditBlog(blog)}
                        className="label-mono hover:text-oxblood transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteBlog(blog.id)}
                        className="label-mono hover:text-oxblood transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}
